import { convertPlayerPrice } from "@/functions/convertPlayerPrice";
import { owned_icon_true, untradeable_icon_true } from "@/icons";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { EventToken, ObjectiveDisplay, PriceInfo } from "@repo/shared/types";
import { useMediaQuery } from "usehooks-ts";

import { usePriceDisplay } from "../Price/usePriceDisplay";

interface PriceProps {
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
  /** Event token catalog for token price icons, supplied by the host. */
  eventTokens?: EventToken[];
  /** Base URL for the coin/token icons (e.g. https://assets.easysbc.io). */
  assetsBaseUrl?: string;
  className?: string;
  /** The pill is text: a phone card gets the small step, not a scaled one. */
  size?: "small" | "medium";
  /** A club card that cannot be sold: the mark sits after the price. */
  untradeable?: boolean;
  /** A card in the user's club: the mark sits after the price. */
  owned?: boolean;
}

export const Price = ({
  priceInfo,
  objectiveDisplay,
  eventTokens,
  assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL,
  size = "medium",
  untradeable = false,
  owned = false,
}: PriceProps) => {
  const { t } = useTranslation("common");
  const isSmallDesktop = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const { iconUrl, label, price, showPrice } = usePriceDisplay({
    priceInfo,
    objectiveDisplay,
    eventTokens,
    assetsBaseUrl,
  });

  return (
    <div
      className={cn(
        "flex items-center gap-x-0.5 border border-neutral-700 bg-neutral-900 px-1 text-white",
        size === "small"
          ? "min-h-5 rounded-md text-sm"
          : "min-h-6 rounded-lg text-base",
      )}
    >
      <div className="flex items-center gap-x-1">
        {iconUrl && (
          <img
            className={cn(
              "shrink-0",
              size === "small"
                ? "h-3.5 w-3.5"
                : "h-3.5 w-3.5 touch-device:h-3.5 touch-device:w-3.5",
            )}
            src={iconUrl}
            alt={t("alt.coin")}
          />
        )}
      </div>
      {label && <div>{label}</div>}
      {showPrice && (
        <div className="font-oswald">
          {convertPlayerPrice(price, isSmallDesktop)}
        </div>
      )}
      {[owned && owned_icon_true, untradeable && untradeable_icon_true].map(
        (icon, i) =>
          icon && (
            <span
              key={i}
              className={cn(
                "ml-0.5 shrink-0 ",
                size === "small" ? "size-3" : "size-3.5",
              )}
            >
              {icon}
            </span>
          ),
      )}
    </div>
  );
};
